/* eslint-disable max-len */
import React, { SyntheticEvent } from 'react';

import { observer } from 'mobx-react';

import { AxiosError } from 'axios';

import { trackHubSpotEvent } from 'utils/hubspot';

import SearchPopover from 'ui/SearchPopover';

import { useSearch } from 'utils/hooks/search';

import { DeviceTypeIDText, DeviceTypeNameText } from 'containers/Devices/components/DeviceTypeCell/styles';

import { useStore } from 'utils/hooks/store';

import Unassign from 'containers/Manage/components/MonitoredDevicesCell/components/UnassignItem';
import DropDownItem from 'containers/Manage/components/MonitoredDevicesCell/components/DropDownItem';
import API from 'utils/api';

import { SharedDeviceResponse } from 'utils/api/modules/devices/devices.types';

import MobileChip from 'components/MobileToggle';

import { LicenseType, LicenseTypeName, Products } from 'core/constants';

import { colors } from 'theme/colors';

import useUpdateUser, { getLicenseType } from 'stores/Manage/hooks/updateUser';

import AddFilled from 'components/Icons/AddFilled';
import Chip from 'components/Chip';
import AdditionalDeviceDropDownItem from 'containers/Manage/components/MonitoredDevicesCell/components/AdditionalDeviceDropDownItem';

import {
  Container,
  DeviceLabel,
  LicenseManagementLink,
  InsufficientLicensesBanner,
  DevicesContainer,
  AdditionalDevicesChipContainer,
} from './styles';
import { MonitoredDevicesCellProps } from './types';

const searchUnusedDevices: typeof API.devices.searchDevices = (params) =>
  API.devices.searchDevices({
    ...params,
    assigned: false,
  });

const MonitoredDevices: React.FunctionComponent<MonitoredDevicesCellProps> = ({
  rowData: { devices, showSafety, phoneNumber },
  rowData,
  session,
  actions,
}) => {
  const { notification } = useStore();

  const [anchor, setAnchor] = React.useState<(EventTarget & Element) | null>(null);
  const [selectedDevice, setSelectedDevice] = React.useState<SharedDeviceResponse | null>(null);
  const [search, setSearch] = React.useState('');
  const [updating, setUpdating] = React.useState(false);
  const [licenseError, setLicenseError] = React.useState<LicenseType | null>(null);
  const updateUser = useUpdateUser();
  const [additionalDevicesAnchorEl, setAdditionalDevicesAnchorEl] = React.useState<(EventTarget & Element) | null>(
    null
  );

  const { results, searchQuery, loading } = useSearch(searchUnusedDevices, Boolean(anchor));

  const chipClick = (event: SyntheticEvent, device?: SharedDeviceResponse): void => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
    if (device) {
      setSelectedDevice(device);
    }
  };

  const close = async (): Promise<void> => {
    setAnchor(null);
    setSelectedDevice(null);
    setSearch('');
    await searchQuery('');
  };

  const update = async (data: { devices?: string[]; showSafety?: boolean }): Promise<void> => {
    try {
      setLicenseError(null);
      setUpdating(true);
      await updateUser(rowData.uuid, data, () => {}, actions ? actions.refreshData : async () => {});
    } catch (e) {
      const licenseType = getLicenseType(e as AxiosError);
      setLicenseError(licenseType);
    } finally {
      close();
      setUpdating(false);
    }
  };

  const unassign = (uuid?: string): void => {
    let newDevices = [];
    if (uuid) {
      newDevices = devices.filter((device) => device.uuid !== uuid).map((device) => device.uuid);
    } else {
      newDevices = devices.filter((device) => device.uuid !== selectedDevice?.uuid).map((device) => device.uuid);
    }
    update({ devices: newDevices });
  };

  const assign = async (item: { uuid: string }): Promise<void> => {
    if (rowData.alarmResponseProfile) {
      const newDevices = [
        ...devices.filter((device) => device.uuid !== selectedDevice?.uuid).map((device) => device.uuid),
        item.uuid,
      ];
      update({ devices: newDevices });
      if (session.hasHubSpot) {
        trackHubSpotEvent('Device or app licence assigned');
      }
    } else {
      notification.enqueueErrorSnackbar('Alarm Response Profile required');
      close();
    }
  };

  const toggleShowSafety = async (): Promise<void> => {
    if (rowData.alarmResponseProfile) {
      await update({ showSafety: !showSafety });
      if (session.hasHubSpot) {
        trackHubSpotEvent('Device or app licence assigned');
      }
    } else {
      notification.enqueueErrorSnackbar('Alarm Response Profile required');
    }
  };

  if (updating) {
    return <Container text>Updating devices...</Container>;
  }

  const onChange = async (s: string): Promise<void> => {
    setSearch(s);
    await searchQuery(s);
  };

  const additionalDevices = devices.slice(1, devices.length).map((device) => ({
    uuid: device.uuid,
    brand: device.type?.brand,
    model: device.type?.model,
    image: device.type?.image,
    name: device.customId,
  }));
  const openAdditionalDevices = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAdditionalDevicesAnchorEl(event.currentTarget);
  };
  const closeAdditionalDevices = (event?: React.MouseEvent<HTMLLIElement>): void => {
    setAdditionalDevicesAnchorEl(null);
    event?.stopPropagation();
  };

  return (
    <Container>
      {licenseError && (
        <InsufficientLicensesBanner>
          Insufficient number of <b>{LicenseTypeName[licenseError]}</b> licenses. Visit
          <LicenseManagementLink href={`${Products.admin}/dashboard/users/`}>
            License Management
          </LicenseManagementLink>{' '}
          section.
        </InsufficientLicensesBanner>
      )}
      <MobileChip toggleShowSafety={toggleShowSafety} showSafety={showSafety} disabled={!phoneNumber && !showSafety} />
      <DevicesContainer>
        {devices.slice(0, 1).map((device) => (
          <Chip
            onClick={(event) => chipClick(event, device)}
            key={device.uuid}
            iconLeft={<img src={device.type?.image} alt={`${device.type?.brand} ${device.type?.model}`} />}
            label={
              <DeviceLabel>
                <DeviceTypeNameText id="device-type-name">{`${device.type?.brand} ${device.type?.model}`}</DeviceTypeNameText>
                <DeviceTypeIDText id="device-type-id">({device.customId})</DeviceTypeIDText>
              </DeviceLabel>
            }
            variant="outlined"
            colors={{
              background: colors.common.white,
              border: colors.divider,
            }}
          />
        ))}
        {additionalDevices.length ? (
          <AdditionalDevicesChipContainer
            onClick={(event) => {
              openAdditionalDevices(event);
            }}
          >
            <Chip
              label={`+${additionalDevices.length}`}
              variant="filled"
              size="small"
              colors={{
                background: colors.action.selected,
                foreground: colors.text.primary,
                hoverBackground: colors.divider,
              }}
              enableHover
              iconLeftGapWidth={8}
              iconRightGapWidth={8}
            />
            <SearchPopover
              id="popover-manage_devices"
              ListItem={AdditionalDeviceDropDownItem}
              handleClose={() => {
                closeAdditionalDevices();
              }}
              handleUpdate={async (el) => {
                unassign(el.uuid);
                closeAdditionalDevices();
              }}
              listData={additionalDevices}
              anchorEl={additionalDevicesAnchorEl as HTMLAnchorElement}
              loading={false}
              customMargin={{
                marginLeft: '16px',
                marginRight: '16px',
              }}
            />
          </AdditionalDevicesChipContainer>
        ) : null}
        <Chip
          aria-label="Add device"
          iconLeft={<AddFilled />}
          onClick={chipClick}
          variant="outlined"
          borderStyle="dashed"
          colors={{
            background: colors.common.white,
            border: colors.action.selected,
            iconLeftBackground: colors.grey[200],
          }}
          label={devices.length === 0 ? 'Add device' : undefined}
        />
      </DevicesContainer>
      <SearchPopover
        ListHeader={
          selectedDevice
            ? () => (
                <Unassign
                  onClick={() => {
                    unassign(selectedDevice.uuid);
                  }}
                />
              )
            : undefined
        }
        ListItem={DropDownItem}
        handleClose={close}
        handleUpdate={assign}
        listData={results}
        search={{
          onChange,
          placeholder: 'search here',
          value: search,
        }}
        anchorEl={anchor as HTMLAnchorElement}
        loading={loading}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      />
    </Container>
  );
};

export default observer(MonitoredDevices);
